import { type HttpInterceptorFn } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { AuthConfig } from 'angular-oauth2-oidc';

export interface EsAuthConfig {
  /** Keycloak base URL — e.g. https://identity.echospaces.cloud */
  keycloakUrl: string;
  /** Keycloak realm name */
  realm: string;
  /** OIDC client ID */
  clientId: string;
  /** Post-login redirect — e.g. / */
  redirectUri: string;
  /** Post-logout redirect — e.g. /login */
  postLogoutRedirectUri: string;
  /** Scopes to request */
  scope: string;
  /**
   * Extra HTTP interceptors registered after the bearer-token one — e.g.
   * `httpErrorToastInterceptor` from es-ui to surface API errors to the user.
   */
  httpInterceptors?: HttpInterceptorFn[];
}

export const ES_AUTH_CONFIG = new InjectionToken<EsAuthConfig>('ES_AUTH_CONFIG');

export function buildOAuthConfig(esConfig: EsAuthConfig): AuthConfig {
  const issuer = `${esConfig.keycloakUrl}/realms/${esConfig.realm}`;
  return {
    issuer,
    clientId: esConfig.clientId,
    redirectUri: esConfig.redirectUri,
    postLogoutRedirectUri: esConfig.postLogoutRedirectUri,
    scope: esConfig.scope || 'openid profile email',
    responseType: 'code',
    showDebugInformation: false,
    requireHttps: false, // Allow HTTP for local dev
    // Authorization Code + PKCE refreshes via the refresh token — NOT the
    // implicit-flow hidden-iframe `prompt=none` silent refresh. With
    // `useSilentRefresh: true`, a first load with no SSO session bounces back
    // from Keycloak as `error=login_required` and — with no dedicated
    // silentRefreshRedirectUri — lands on the app's redirect URI as a fatal
    // code_error instead of letting the guard do interactive login.
    // `setupAutomaticSilentRefresh()` uses the refresh token when this is off.
    useSilentRefresh: false,
  };
}
